import React, { useCallback, useState } from 'react';

import { Button, Divider, Flex, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import KiloButton from 'components/KiloButton';
import LastCreditAlertModal from 'components/LastCreditAlertModal';
import { useNavigate } from 'react-router-dom';
import { AppointmentRetrieve } from 'types';
import { LastCreditAlertContent } from 'utils/last-credit-alert';

import LateCancelNoShowAthleteAppointments from './components/LateCancelAndNoShowAthletes';
import ModalCreateAthleteAppointment from './components/ModalCreateAthleteAppointment';
import ReservedAndCheckedInAthletes from './components/ReservedAndCheckedInAthletes';
import { AddAthleteState } from './types';

interface IReservationsFormProps {
  appointment: AppointmentRetrieve;
  handleCloseDrawer: () => void;
  permission?: boolean;
}

const ReservationsForm = ({
  appointment,
  permission = true,
  handleCloseDrawer,
}: IReservationsFormProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [lateCancelOpened, { toggle: handleLateCancelToggle }] = useDisclosure();
  const reservedAndCheckedInAthletes = appointment.athleteAppointments.filter(
    aa => (aa.isCheckedIn || aa.isReserved) && !aa.isLateCancel && !aa.isNoShow,
  );
  const lateCancelNoShowAthletes = appointment.athleteAppointments.filter(
    aa => aa.isLateCancel || aa.isNoShow,
  );
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [addModalType, setAddModalType] = useState<AddAthleteState>('attended');

  const [lastCreditAlert, setLastCreditAlert] = useState<LastCreditAlertContent | null>(null);

  const handleOpenAddModal = (type: AddAthleteState): void => {
    setAddModalType(type);
    openAddModal();
  };

  const redirectToKioskCheckIn = useCallback((): void => {
    navigate(`/kiosk/${appointment.appointmentId}?eventType=appointment&from=calendar`);
  }, [appointment, navigate]);

  return (
    <Stack spacing='xs'>
      <Group grow pb='sm'>
        <Flex direction={'row'} gap={'md'}>
          <Text fz='lg' fw='600'>
            Reserved
          </Text>
          <KiloButton
            onClick={() => redirectToKioskCheckIn()}
            variant='outline'
            fw={600}
            py={3}
            px={7}
            h={28}
          >
            Kiosk Check In
          </KiloButton>
        </Flex>
        <div />
      </Group>
      <ReservedAndCheckedInAthletes
        athleteAppointments={reservedAndCheckedInAthletes}
        handleCloseDrawer={handleCloseDrawer}
        permission={permission}
        setLastCreditAlert={setLastCreditAlert}
      />
      <Group position='center' p='lg'>
        <Button
          leftIcon={<IconPlus size={theme.fontSizes.sm} />}
          onClick={() => handleOpenAddModal('reserved')}
          disabled={!permission}
        >
          Reserved Athlete
        </Button>
        <Button
          leftIcon={<IconPlus size={theme.fontSizes.sm} />}
          onClick={() => handleOpenAddModal('attended')}
          disabled={!permission}
        >
          Attended Athlete
        </Button>
      </Group>
      <Divider mt={0} mb='md' mx='-xl' />
      <ExpandableSection
        title='Late Cancel/No Show'
        opened={lateCancelOpened}
        handleToggle={handleLateCancelToggle}
      >
        <LateCancelNoShowAthleteAppointments
          athleteAppointments={lateCancelNoShowAthletes}
          totalAthleteAppointmentsLength={appointment.athleteAppointments.length}
          handleCloseDrawer={handleCloseDrawer}
          permission={permission}
        />
      </ExpandableSection>
      <ModalCreateAthleteAppointment
        opened={addModalOpened}
        handleClose={closeAddModal}
        type={addModalType}
        appointmentId={appointment.appointmentId}
        setLastCreditAlert={setLastCreditAlert}
      />
      <LastCreditAlertModal alert={lastCreditAlert} onClose={() => setLastCreditAlert(null)} />
    </Stack>
  );
};

export default ReservationsForm;
